/**
 * Chart plumbing shared by the Access observability panels.
 *
 * Kept out of the components so the fast-refresh lint rule stays happy, and so
 * the "which label names this series" decision is written once: a panel says
 * `labelKeys={['app']}` and gets one line per app, with the same colour order
 * everywhere.
 */

/**
 * The interface filter every `node_network_*` query uses (VSNP-1262).
 *
 * One constant because the fleet column and the detail chart disagreed, and
 * both were wrong in a way that was invisible:
 *
 *   fleet   iface!~"lo|tun.*"   detail   iface!~"lo"
 *
 * PromQL regex matchers are ANCHORED, so `tun.*` matches `tun0` and nothing
 * else. The fleet column's own header said "excluding tunnel interfaces" while
 * happily including `gw-tun`, `acntun0` and `acn-tun` — which is to say, on a
 * gateway carrying full-tunnel traffic it counted the same bytes twice: once
 * encrypted on the physical NIC, once in the clear on the tunnel.
 *
 * So the rule is: exclude loopback and EVERY tunnel interface, on both
 * surfaces. The fleet number is then a real NIC throughput, and — because the
 * detail chart draws the same set per interface — the detail lines add up to
 * the fleet number, which is the property that makes the two comparable at
 * all. Tunnel throughput is not lost: agw_flow_bytes_total measures it
 * per app and per tenant, which is the honest way to ask that question.
 */
export const NODE_IFACE_FILTER = 'iface!~"lo|.*tun.*"';

/** Human wording for the filter above, so a tooltip cannot drift from it. */
export const NODE_IFACE_FILTER_LABEL = 'excluding loopback and tunnel interfaces';

export const CHART_COLORS = [
  '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#e11d48',
];

/** Per-action colours, so "deny" is red on every chart that splits by action. */
export const ACTION_COLORS: Record<string, string> = {
  allow: '#10b981',
  deny: '#ef4444',
  error: '#f59e0b',
};

/**
 * Name a series from its labels.
 *
 * Falls back to the whole label set rather than to a generic "value": two
 * unnamed lines on one chart that both read "value" is worse than a long label.
 */
export function seriesLabel(metric: Record<string, string>, keys?: string[]): string {
  if (keys?.length) {
    const parts = keys.map((k) => metric[k]).filter(Boolean);
    if (parts.length) return parts.join(' · ');
  }
  const rest = Object.entries(metric).filter(([k]) => k !== '__name__' && k !== 'node_kind' && k !== 'gateway_id' && k !== 'connector_id');
  if (rest.length === 0) return metric.__name__ ?? 'value';
  return rest.map(([, v]) => v).join(' · ');
}

/** Recharts line descriptors for every numeric key present in the rows. */
export function dynamicLines(
  data: Array<Record<string, number>>,
  colorFor?: (key: string) => string | undefined,
): { dataKey: string; name: string; color: string }[] {
  if (!data || data.length === 0) return [];
  const keys = new Set<string>();
  for (const row of data) {
    for (const k of Object.keys(row)) if (k !== 'timestamp') keys.add(k);
  }
  return Array.from(keys).map((k, i) => ({
    dataKey: k,
    name: k,
    color: colorFor?.(k) ?? CHART_COLORS[i % CHART_COLORS.length],
  }));
}

/** Human bytes. Kept here so panels and tables agree on the same rounding. */
export function fmtBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = Math.abs(bytes);
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 100 || i === 0 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
}

/** Short duration for a session row: 2.3s, 4m 12s, 1h 04m. */
export function fmtDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return '—';
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 2 : 0)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return `${m}m ${String(s).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, '0')}m`;
}

/**
 * design §5.2 — the events marked "Security-relevant: yes" other than
 * `flow.deny`, which is reached through `action=deny` and so covers L3/L4/L7
 * denies in one query.
 *
 * `flow.abandon` (VSNP-1427, access-common faaf37d) is not a refusal: it is a
 * flow the gateway held for hostname classification that ended with NO
 * decision — its handshake never completed, or the tunnel closed first. Until
 * the gateway wrote it, such an attempt left no trace anywhere, and it sits
 * with the denies because "something tried to reach a host that never
 * answered" is what an investigation on this page is looking for. It carries
 * no `action`, so an `action=deny` query can never return it; it is fetched by
 * name like the others.
 */
export const SECURITY_EVENTS = [
  'flow.abandon',
  'session.reject',
  'posture.reject',
  'connector.refuse',
  'l3.spoof_drop',
  'policy.rejected',
] as const;

/**
 * Everything the Security view unions — `flow.deny` plus SECURITY_EVENTS — in
 * one place, because the table and the export beneath it must ask for the same
 * set (doc 22 §10.1): an export that covered less than the screen would hand an
 * auditor a subset without saying so.
 */
export const SECURITY_EVENT_UNION = ['flow.deny', ...SECURITY_EVENTS] as const;

/**
 * What a security event means, for the tooltip on its name in tables and
 * facets. The name itself stays what is shown — it is what LogQL, the CSV and
 * the JSONL export carry — so the words live here, once.
 */
export const AUDIT_EVENT_DESCRIPTIONS: Record<string, string> = {
  'flow.deny': 'The policy denied this flow.',
  'flow.abandon':
    'Held for hostname classification and ended with NO decision — the handshake never completed, or the tunnel ' +
    'closed first. Nothing was allowed or denied: the provisional packets were forwarded.',
  'session.reject': 'The gateway turned this session away.',
  'posture.reject': 'A posture document failed verification.',
  'connector.refuse': 'A connector asked for an app it was not assigned.',
  'l3.spoof_drop': 'An inner packet claimed a source it had not been leased.',
  'policy.rejected': 'A gateway refused a policy bundle.',
};
